<template>
    <UseTicketSubmit>
        <template #default="{ removeError, isTicketSubmitting, isInTicketNegotiation, submitErrors, submitTicket }">
            <div
                v-if="!isMobile && isVisible"
                id="betslip"
                v-sticky="stickyConfig"
            >
                <CheckTicket
                    v-if="isTicketCheckVisible"
                    class="check-ticket"
                />
                <BetSlipEmptyPlaceholder
                    v-if="isEmpty && !isTicketSubmitting"
                    class="betslip-placeholder"
                />
                <SdsBetSlipDesktop
                    v-else
                    :isTicketCheckVisible="isTicketCheckVisible"
                    :isTicketSubmitting="isTicketSubmitting"
                    :isTicketInNegotiation="isInTicketNegotiation"
                    :submitErrors="submitErrors"
                    :submitTicketExternal="submitTicket"
                    @onTabChange="onTabChange"
                    @error:remove="removeError"
                />
                <div v-if="isEmpty">
                    <PE92FixedBasedOn
                        basedOn=".betslip-placeholder"
                        :below="30"
                    >
                        <PE92InlineDepositForm
                            positioning="betSlip"
                            style="width: 320px;"
                        />
                    </PE92FixedBasedOn>
                </div>
            </div>
            <div
                v-else-if="isVisible"
                class="compact-betslip-area"
            >
                <div class="betslip-errors">
                    <Errors
                        v-if="selectionAddError"
                        :errors="[selectionAddError]"
                        :areValidationErrors="true"
                        :isWarning="true"
                        :shouldFadeOut="true"
                        @fadeOut="setSelectionAddError(null)"
                    />
                    <Errors
                        v-if="errors"
                        :errors="errors"
                        :areValidationErrors="true"
                        :isWarning="false"
                        :shouldFadeOut="false"
                        :isClosable="false"
                    />
                </div>
                <SdsQuickBetslip
                    v-if="isQuickBetslipShown"
                    :isBetslipExpanded="isPreviewOpen"
                    :isTicketSubmitting="isTicketSubmitting"
                    :isInTicketNegotiation="isInTicketNegotiation"
                    :submitErrors="submitErrors"
                    :submitTicketExternal="submitTicket"
                />
                <BetSlipCompact
                    v-else
                    :isInTicketNegotiation="isInTicketNegotiation"
                />
            </div>
        </template>
    </UseTicketSubmit>
</template>

<script>
    import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
    import { getConfig } from '@superbet-group/web.lib.config';
    import { isMobileLayout } from '@superbet-group/web.lib.vue-utilities';
    import { featureFlagService } from '@superbet-group/web.lib.flags';
    import {
        mapActions,
        mapGetters,
    } from 'vuex';

    import BetSlipCompact from '@app/mobile/shared/components/BetSlipCompact/BetSlipCompactContainer';
    import { getIsTicketCheckVisible } from '@app/utils/config/sportConfig';
    import BetSlipEmptyPlaceholder from '@shared/betSlip/BetSlipEmptyPlaceholder.vue';
    import Errors from '@shared/betSlip/Errors';
    import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
    import { getFederationComponent } from '@src/federations/getFederationComponent';
    import { Product } from '@app/router/products/Product';
    import { ticketLogger } from '@ticketFederation/ticketLogger';

    export default {
        name: 'BetSlipContainer',
        props: {
            hasSystems: {
                type: Boolean,
                required: false,
                default: true,
            },
            hasStickyBetSlip: {
                type: Boolean,
                required: false,
                default: true,
            },
        },
        data() {
            return {
                isQuickBetslipExperimentOn: false,
            };
        },
        created() {
            this.isTicketCheckVisible = getIsTicketCheckVisible();
            this.isVisible = Product.Sport;
            this.quickBetslipUnsubscribeFn = featureFlagService.subscribeToFeatureChanges('bex.betslip.quick-betslip-web', (isExperimentOn) => {
                this.isQuickBetslipExperimentOn = isExperimentOn;
            }, false);
        },
        errorCaptured(err) {
            ticketLogger.error(err);
        },
        computed: {
            ...mapGetters('ui/modal', [
                'modal',
            ]),
            betSlipModalCode() {
                return modalCodes.mobileBetSlip;
            },
            isPreviewOpen() {
                return this.modal?.code === this.betSlipModalCode;
            },
            isQuickBetslipShown() {
                if (!this.isQuickBetslipExperimentOn) {
                    return false;
                }

                return this.selections.length === 1
                    && this.selections[0].isOngoing()
                    && this.isSimple
                    && this.isOnline;
            },
            ...mapGetters('ui/sportOffer/betSlip', [
                'selectionAddError',
                'selections',
                'isSimple',
                'isOnline',
                'isEmpty',
                'errors',
            ]),
            isMobile() {
                return isMobileLayout();
            },
            isSuperBonusFeatureEnabled() {
                return getConfig().superBonus.isEnabled;
            },
            stickyConfig() {
                return {
                    scrollableContentSelector: '.sds-betslip-desktop',
                    staticLowerContentSelector: '.betslip__static__bottom',
                    staticHigherContentSelector: '.verification-bar',
                    fixedLowerContentSelector: '.ticket-stack__container .ss-chat',
                    offset: 26,
                };
            },
        },
        methods: {
            ...mapActions('ui/sportOffer/betSlip', [
                'setSelectionAddError',
            ]),
            onTabChange(type) {
                AnalyticsSDK.getInstance().log('Betslip_Type', {
                    type,
                    eventLabel: type,
                });
            },
        },
        destroyed() {
            this.quickBetslipUnsubscribeFn?.();
        },
        components: {
            UseTicketSubmit: getFederationComponent(() => import('tickets/UseTicketSubmit'), ticketLogger),
            CheckTicket: getFederationComponent(() => import('tickets/CheckTicket')),
            BetSlipEmptyPlaceholder,
            SdsBetSlipDesktop: () => import('@betslipFederation/components/SdsBetSlipDesktop.vue'),
            SdsQuickBetslip: () => import('@betslipFederation/components/SdsQuickBetslip.vue'),
            BetSlipCompact,
            Errors,
            PE92FixedBasedOn: () => import('@walletFederation/src/components/PE92FixedBasedOn.vue'),
            PE92InlineDepositForm: () => import('@walletFederation/src/components/PE92InlineDepositForm.vue'),
        },
    };
</script>

<style lang="scss" scoped>
    @import '~@superbet-group/web.lib.global-styles/config';

    $betslip-errors-padding: 8px;

    .betslip-placeholder {
        --header-height: var(--new-desktop-header-height);
        --layout-padding-top: 16px;
        --layout-padding-bottom: 24px;
        --verify-ticket-height: 76px;
        position: fixed;
        top: calc(var(--header-height) + var(--layout-padding-top) + var(--layout-padding-bottom));
        width: 320px;

        &:not(:first-child) {
            top: calc(var(--header-height) + var(--layout-padding-top) + var(--layout-padding-bottom) + var(--verify-ticket-height));
        }
    }

    .compact-betslip-area {
        &::v-deep {
            .betslip-error {
                margin-bottom: 4px;
                border-radius: 4px;
                line-height: 18px;

                .icon-close {
                    top: 9px;
                }
            }
        }
    }

    .betslip-errors {
       padding: 0 $betslip-errors-padding;
       padding-left: calc(max(#{$betslip-errors-padding}, env(safe-area-inset-left)));
       padding-right: calc(max(#{$betslip-errors-padding}, env(safe-area-inset-right)));
    }

</style>
